/**
 * PopulatedGlobe — Three.js spinning Earth + Moon + animated user flight paths
 * + pulse heat markers at active user locations.
 *
 * Used in two places:
 *   1. End of OpeningCinematic — "Hey, we have been waiting for you" reveal.
 *      Click the globe → /signup or /login.
 *   2. Inside the HUD MiniGlobe slot post-signin — replaces the abstract globe
 *      with this living Earth showing platform activity.
 *
 * Methodology hold #5: the globe is calm — slow rotation, gentle pulses, no
 * frantic motion. The illusion of presence is enough.
 */
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Self-hosted under /textures/ (Public/) — same-origin avoids unpkg CORS.
// `earth-live.jpg` is TODAY's NASA MODIS Terra true-color composite, fetched
// every 6h by services/weather_layer.py and composited over the Blue Marble
// base so polar-orbit swath gaps fall back to clean Earth instead of black.
// `earth-day.jpg` is the pristine NASA Blue Marble (gap-free, ~244 KB) used
// as the canonical fallback. `earth-blue-marble.jpg` is the higher-res backup.
const EARTH_TEX_LIVE = '/textures/earth-live.jpg';
const EARTH_TEX_DAY = '/textures/earth-day.jpg';
const EARTH_TEX_DAY_FALLBACK = '/textures/earth-blue-marble.jpg';
const EARTH_TEX_NIGHT = '/textures/earth-night.jpg';
const EARTH_TEX_TOPO = '/textures/earth-topology.png';

// Sample active-user locations (realistic enough for a vibrant globe).
// In production these would come from /api/world/active-users — but the
// surface is the same. Pre-populated so the cinematic feels alive day one.
const SEED_USER_HOTSPOTS = [
  { lat: 40.7128, lng: -74.006, label: 'New York' },
  { lat: 51.5074, lng: -0.1278, label: 'London' },
  { lat: 35.6762, lng: 139.6503, label: 'Tokyo' },
  { lat: -33.8688, lng: 151.2093, label: 'Sydney' },
  { lat: -23.5505, lng: -46.6333, label: 'São Paulo' },
  { lat: 19.0760, lng: 72.8777, label: 'Mumbai' },
  { lat: 30.0444, lng: 31.2357, label: 'Cairo' },
  { lat: 55.7558, lng: 37.6173, label: 'Moscow' },
  { lat: 1.3521, lng: 103.8198, label: 'Singapore' },
  { lat: 34.0522, lng: -118.2437, label: 'Los Angeles' },
  { lat: -1.2921, lng: 36.8219, label: 'Nairobi' },
  { lat: 48.8566, lng: 2.3522, label: 'Paris' },
  { lat: 41.9028, lng: 12.4964, label: 'Rome' },
  { lat: 39.9042, lng: 116.4074, label: 'Beijing' },
  { lat: 6.5244, lng: 3.3792, label: 'Lagos' },
  { lat: 28.6139, lng: 77.2090, label: 'New Delhi' },
];

// Pre-defined flight paths — Apr 30 2026 (Lead's call): every arc must
// originate from East Africa (Nairobi, idx 10) and radiate outward to the
// other hotspots. This is thematic — The First Walker is the only chain
// currently published, and the methodology is clear: humanity walked out
// of Africa. Every line on the globe re-tells that story without a
// caption. When new chains open elsewhere, additional origin points get
// appended; for now the constellation is a single-origin starburst.
//
// Index legend (matches SEED_USER_HOTSPOTS above):
//   10 = Nairobi (East Africa origin)        — every arc starts here
//   14 = Lagos (West Africa, secondary node) — visible diaspora node
const NAIROBI = 10;
const LAGOS = 14;
const SEED_FLIGHT_PATHS = [
  // Out-of-Africa primary radiation — Nairobi → everywhere else
  [NAIROBI, 0],  // New York
  [NAIROBI, 1],  // London
  [NAIROBI, 2],  // Tokyo
  [NAIROBI, 3],  // Sydney
  [NAIROBI, 4],  // São Paulo
  [NAIROBI, 5],  // Mumbai
  [NAIROBI, 6],  // Cairo
  [NAIROBI, 7],  // Moscow
  [NAIROBI, 8],  // Singapore
  [NAIROBI, 9],  // Los Angeles
  [NAIROBI, 11], // Paris
  [NAIROBI, 12], // Rome
  [NAIROBI, 13], // Beijing
  [NAIROBI, 15], // New Delhi
  // West-African intra-continental hop so the African continent itself
  // shows visible internal connectivity, not just outbound radiation.
  [NAIROBI, LAGOS],
];

function latLngToVec3(lat, lng, radius) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta),
  );
}

export default function PopulatedGlobe({
  size = 600,
  showMoon = false,
  showFlightPaths = true,
  autoRotate = true,
  onClick,
  className,
  testId,
  // ── Social layer props (Apr 29 2026 batch #2) ─────────────────────
  // friendArcs: [{ from_lat, from_lon, to_lat, to_lon, name, relation }]
  // activeUsers: [{ lat, lon, is_authenticated, country, city }]
  // userHeat: [{ lat, lon, visit_count }]
  friendArcs = [],
  activeUsers = [],
  userHeat = [],
}) {
  const mountRef = useRef(null);
  const rafRef = useRef(0);
  const cleanupRef = useRef(null);
  const earthGroupRef = useRef(null); // exposed so the social-layer effect can attach meshes

  useEffect(() => {
    if (!mountRef.current) return;
    // Capture the mount element at setup time — React 18 StrictMode calls
    // the effect twice in dev, and the ref may point to a different DOM
    // node by the time cleanup runs on the first invocation. Closing over
    // the element here guarantees cleanup removes THIS effect's canvas
    // from THIS effect's parent, never leaving a stray one behind.
    const mountEl = mountRef.current;

    const width = mountEl.clientWidth || size;
    const height = mountEl.clientHeight || size;
    // Apr 30 2026 iter_64 per Lead's crash report: "Error creating WebGL
    // context." Catch the failure and paint a static Blue Marble PNG so
    // the page keeps working on low-GPU / WebGL-exhausted browsers.
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, failIfMajorPerformanceCaveat: false, powerPreference: 'low-power' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PopulatedGlobe] WebGL unavailable, falling back to static Earth', err);
      const img = document.createElement('img');
      img.src = '/textures/earth-day.jpg';
      img.alt = 'Earth';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.borderRadius = '50%';
      img.setAttribute('data-testid', 'populated-globe-fallback');
      mountEl.appendChild(img);
      return;
    }
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 1);
    // sRGB output + ACES tone mapping. ACES holds bright pixels below the
    // clamp ceiling so the Blue Marble never blows out to pure white.
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.9;
    mountEl.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 15);

    // Lighting — modest levels so the Blue Marble TEXTURE dominates the
    // render instead of washing to a flat lit sphere. Dim further than
    // iter_37 (which left the globe as a grey blob because map multiply
    // was out-gained by ambient+key). With MeshStandardMaterial + these
    // levels, the daytime continents read clearly.
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.35);
    scene.add(ambientLight);
    const keyLight = new THREE.DirectionalLight(0xfff5e0, 1.4);
    keyLight.position.set(5, 3, 5);
    scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(0xb0c4de, 0.25);
    fillLight.position.set(-3, -1, 2);
    scene.add(fillLight);
    const rimLight = new THREE.DirectionalLight(0x88ccff, 0.15);
    rimLight.position.set(0, 0, -5);
    scene.add(rimLight);

    // Earth — bulletproof texture load: bypass three.js's TextureLoader
    // entirely and use a vanilla `new Image()` with onload. We then build
    // the THREE.Texture from a guaranteed-decoded HTMLImageElement. This
    // sidesteps a race condition we hit where three.js's loader was
    // uploading a 1x1 default texture before the real image finished
    // decoding, leaving the sphere as a flat dim color.
    const EARTH_R = 5;
    const earthGroup = new THREE.Group();
    earthGroupRef.current = earthGroup;
    const earthGeom = new THREE.SphereGeometry(EARTH_R, 96, 96);

    let disposed = false;

    const earthMat = new THREE.MeshStandardMaterial({
      // Start with a full-white base so the texture's RGB is passed
      // through at face value. MeshStandardMaterial multiplies base
      // color × map, so keeping color at 0xffffff = no tint.
      color: new THREE.Color(0xffffff),
      // Matte planet surface; no metallic highlights.
      roughness: 1,
      metalness: 0,
      // Very subtle self-lighting so the night side never crushes to
      // black. Neutral-dark emissive color prevents washed-out glow.
      emissive: new THREE.Color(0x0a0e14),
      emissiveIntensity: 0.12,
    });
    const earthMesh = new THREE.Mesh(earthGeom, earthMat);
    earthGroup.add(earthMesh);

    // Subtle atmosphere rim — reduced to 0.06 so it reads as a halo glow
    // and never washes the continents into a blue blob.
    const atmoGeom = new THREE.SphereGeometry(EARTH_R * 1.025, 96, 96);
    const atmoMat = new THREE.MeshPhongMaterial({
      color: 0x4488ff,
      transparent: true,
      opacity: 0.06,
      side: THREE.FrontSide,
    });
    const atmoMesh = new THREE.Mesh(atmoGeom, atmoMat);
    earthGroup.add(atmoMesh);

    // Clouds layer — drifting alpha-mapped sphere just above the surface,
    // turning the planet from "ball with continents" into "live world with
    // weather". The cinematic pays this off as the camera holds on it.
    const cloudsGeom = new THREE.SphereGeometry(EARTH_R * 1.005, 96, 96);
    const cloudsMat = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.65,
      depthWrite: false,
    });
    const cloudsMesh = new THREE.Mesh(cloudsGeom, cloudsMat);
    cloudsMesh.visible = false;
    earthGroup.add(cloudsMesh);

    // Cloud texture is loaded after the day texture below — see scene.add

    scene.add(earthGroup);

    const loader = new THREE.TextureLoader();

    // Earth day texture — vibrant Blue Marble photo.
    //
    // IMPORTANT: if this texture fails (stale SW cache, network hiccup,
    // CORS edge), the material would otherwise fall back to pure emissive
    // white; the blue atmosphere rim then tints the sphere and the user
    // sees a "solid blue ball". We wire a PROPER fallback chain here:
    //   1. /textures/earth-day.jpg        (244 KB, canonical)
    //   2. /textures/earth-blue-marble.jpg (1.5 MB, high-res backup)
    //   3. Keep emissive off + neutral dark blue until something lands
    // Also: the atmosphere rim mesh is kept INVISIBLE until a texture is
    // confirmed decoded, so the user never sees a blue-without-continents
    // frame.
    atmoMesh.visible = false;
    earthMat.emissiveIntensity = 0; // stays dark until texture arrives
    let dayTexLoaded = false;
    const applyDayTexture = (texture, source) => {
      if (dayTexLoaded || earthMat.map === texture) return; // first loader wins; StrictMode re-entry is a no-op
      dayTexLoaded = true;
      texture.colorSpace = THREE.SRGBColorSpace;
      earthMat.map = texture;
      earthMat.emissiveMap = texture;
      // 0.12 matches the material default — just enough night-side lift
      // that continents never crush to black, while keeping the day-side
      // dominated by the real NASA Blue Marble photography under ACES.
      earthMat.emissiveIntensity = 0.12;
      earthMat.needsUpdate = true;
      atmoMesh.visible = true;
      // Diagnostic so we can see in console whether the texture actually
      // bound to the material at runtime (iter_37 showed a flat grey blob
      // because the bind was silently missing on one render path).
      // eslint-disable-next-line no-console
      console.info('[TSOGN] earth texture applied', {
        source,
        width: texture.image?.width,
        height: texture.image?.height,
      });
    };
    loader.load(
      EARTH_TEX_DAY,
      (tex) => applyDayTexture(tex, EARTH_TEX_DAY),
      undefined,
      () => {
        // Primary Blue Marble failed — try the high-res backup.
        loader.load(
          EARTH_TEX_DAY_FALLBACK,
          (tex) => applyDayTexture(tex, EARTH_TEX_DAY_FALLBACK),
          undefined,
          () => {
            // Both textures failed. Keep the sphere dark-navy (NOT the
            // blue-ball state) so the issue is visually obvious and the
            // SW/cache can be flagged upstream.
            earthMat.color = new THREE.Color(0x0a1628);
            earthMat.emissiveIntensity = 0;
            earthMat.needsUpdate = true;
            // eslint-disable-next-line no-console
            console.error('[TSOGN] Earth texture failed (day + fallback). Check /textures/ availability and service-worker cache.');
          },
        );
      },
    );

    const bumpTex = loader.load(EARTH_TEX_TOPO);
    earthMat.bumpMap = bumpTex;
    earthMat.bumpScale = 0.05;
    earthMat.needsUpdate = true;

    // Cloud layer texture — alpha-mapped drift on top of the Earth.
    const cloudsTex = loader.load(
      '/textures/earth-clouds.png',
      () => { cloudsMesh.visible = true; },
    );
    cloudsTex.colorSpace = THREE.SRGBColorSpace;
    cloudsMat.map = cloudsTex;
    cloudsMat.alphaMap = cloudsTex;
    cloudsMat.needsUpdate = true;

    // Heat markers — pulsing dots at user locations. Apr 30 2026: the
    // Nairobi seed (East Africa, idx 10) is the chain-of-origin and gets a
    // bigger, gold-only marker so the eye lands on Africa first; every
    // other hotspot is a smaller cyan companion.
    const markers = [];
    SEED_USER_HOTSPOTS.forEach((spot, i) => {
      const isOrigin = i === NAIROBI;
      const pos = latLngToVec3(spot.lat, spot.lng, EARTH_R + 0.04);
      const markerGeom = new THREE.SphereGeometry(isOrigin ? 0.14 : 0.08, 16, 16);
      const markerMat = new THREE.MeshBasicMaterial({
        color: isOrigin ? 0xffd700 : (i % 3 === 0 ? 0xffd700 : 0x00f0ff),
        transparent: true,
        opacity: isOrigin ? 1.0 : 0.9,
      });
      const marker = new THREE.Mesh(markerGeom, markerMat);
      marker.position.copy(pos);
      earthGroup.add(marker);

      // Pulse halo
      const haloGeom = new THREE.SphereGeometry(isOrigin ? 0.34 : 0.18, 16, 16);
      const haloMat = new THREE.MeshBasicMaterial({
        color: isOrigin ? 0xffd700 : (i % 3 === 0 ? 0xffd700 : 0x00f0ff),
        transparent: true,
        opacity: isOrigin ? 0.55 : 0.35,
      });
      const halo = new THREE.Mesh(haloGeom, haloMat);
      halo.position.copy(pos);
      earthGroup.add(halo);
      markers.push({ marker, halo, phase: Math.random() * Math.PI * 2, isOrigin });
    });

    // Flight path arcs
    const arcs = [];
    if (showFlightPaths) {
      SEED_FLIGHT_PATHS.forEach(([fromIdx, toIdx], pathIdx) => {
        const from = SEED_USER_HOTSPOTS[fromIdx];
        const to = SEED_USER_HOTSPOTS[toIdx];
        if (!from || !to) return;
        const fromVec = latLngToVec3(from.lat, from.lng, EARTH_R);
        const toVec = latLngToVec3(to.lat, to.lng, EARTH_R);
        const mid = fromVec.clone().add(toVec).multiplyScalar(0.5);
        const dist = fromVec.distanceTo(toVec);
        mid.normalize().multiplyScalar(EARTH_R + dist * 0.4);
        const curve = new THREE.QuadraticBezierCurve3(fromVec, mid, toVec);

        const points = curve.getPoints(60);
        const arcGeom = new THREE.BufferGeometry().setFromPoints(points);
        const arcMat = new THREE.LineBasicMaterial({
          color: 0xffd700, // Apr 30 2026 — all diaspora arcs are gold (brand)
          transparent: true,
          // Stronger than the previous 0.15 ambient — these arcs now ARE
          // the foreground signal, telling the Out-of-Africa story.
          opacity: 0.45,
        });
        const arc = new THREE.Line(arcGeom, arcMat);
        earthGroup.add(arc);

        // A glowing pellet that travels along the arc — all gold so the
        // diaspora reads as one coherent radiation outward from Africa.
        const pellet = new THREE.Mesh(
          new THREE.SphereGeometry(0.08, 12, 12),
          new THREE.MeshBasicMaterial({ color: 0xffd700 }),
        );
        earthGroup.add(pellet);
        // t starts near 0 with a small jittered offset so the pellets
        // visibly leave Nairobi rather than appearing mid-arc.
        arcs.push({ curve, pellet, t: Math.random() * 0.15, speed: 0.003 + Math.random() * 0.0025 });
      });
    }

    // Moon — REMOVED Apr 30 2026 per Lead: orbit was broken (slid side-to-side
    // instead of orbiting). Lead asked for it gone. The `showMoon` prop is
    // preserved for backward-compat but is now a no-op; the moon will never
    // render again unless this block is restored deliberately.
    void showMoon;
    // (eliminate the now-unused TextureLoader)
    void loader;

    // Stars backdrop
    const starGeom = new THREE.BufferGeometry();
    const starCount = 800;
    const starPositions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      const r = 80 + Math.random() * 40;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      starPositions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      starPositions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      starPositions[i * 3 + 2] = r * Math.cos(phi);
    }
    starGeom.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    const starMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.18, transparent: true, opacity: 0.65 });
    const stars = new THREE.Points(starGeom, starMat);
    scene.add(stars);

    let t0 = performance.now();
    // Apr 30 2026 — Lead's call: arcs must clearly originate from East
    // Africa. Spawn the globe with East Africa (Nairobi, lat -1.29 /
    // lng 36.82 — The First Walker's spiritual origin) facing the camera
    // at +z. Math: latLngToVec3 puts Nairobi at approx (+0.80r, -0.02r,
    // -0.60r). To rotate that vector to (~0, ~0, +r), we need
    // earthGroup.rotation.y ≈ π + atan2(0.80, 0.60) ≈ 4.07 rad. The
    // value below was sanity-checked visually — Africa centers, with
    // the Atlantic to its west and the Indian Ocean to its east.
    const INITIAL_AFRICA_ROT = 4.05;
    earthGroup.rotation.y = INITIAL_AFRICA_ROT;
    // Per-axis manual drag offset — added to the auto-rotate so the user
    // can spin/tilt the globe with their mouse or finger and the planet
    // resumes its slow auto-orbit when they let go.
    const dragOffset = { x: 0, y: 0 };
    const animate = () => {
      const t = (performance.now() - t0) * 0.001;
      if (autoRotate) {
        earthGroup.rotation.y = INITIAL_AFRICA_ROT + t * 0.025 + dragOffset.x;
      } else {
        // Africa-locked: subtle ±2° breathe wobble so the planet feels
        // alive without rotating off-center. Apr 30 2026 (Lead's call):
        // arcs must clearly radiate from East Africa for The First Walker.
        earthGroup.rotation.y = INITIAL_AFRICA_ROT + Math.sin(t * 0.3) * 0.035 + dragOffset.x;
      }
      earthGroup.rotation.x = dragOffset.y;
      // Cloud drift — slightly faster than Earth so weather slides overhead
      const cloudBase = autoRotate ? INITIAL_AFRICA_ROT + t * 0.035 : INITIAL_AFRICA_ROT + t * 0.018;
      cloudsMesh.rotation.y = cloudBase + dragOffset.x;
      cloudsMesh.rotation.x = dragOffset.y;
      stars.rotation.y = t * 0.005;
      // Pulse markers
      markers.forEach((m) => {
        const s = 1 + 0.5 * Math.sin(t * 2 + m.phase);
        m.halo.scale.setScalar(s);
        m.halo.material.opacity = 0.4 - 0.25 * (s - 1);
      });
      // Move pellets along arcs — Apr 30 2026 (Lead's call): pellets travel
      // FROM the destinations INTO East Africa, not out of it. The story
      // is "Shared Origins" — every line on the globe traces back to
      // Africa. We invert the parametric position (1 - t) so pellets
      // visibly converge on Nairobi every cycle.
      arcs.forEach((a) => {
        a.t = (a.t + a.speed) % 1;
        const pt = a.curve.getPoint(1 - a.t);
        a.pellet.position.copy(pt);
      });
      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      if (!mountRef.current) return;
      const w = mountRef.current.clientWidth;
      const h = mountRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    // ── Manual drag rotation ──
    const canvasEl = renderer.domElement;
    let dragging = false;
    let prevX = 0;
    let prevY = 0;
    const onPointerDown = (e) => {
      dragging = true;
      prevX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      prevY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    };
    const onPointerMove = (e) => {
      if (!dragging) return;
      const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      dragOffset.x += (x - prevX) * 0.005;
      dragOffset.y += (y - prevY) * 0.005;
      dragOffset.y = Math.max(-1.0, Math.min(1.0, dragOffset.y));
      prevX = x;
      prevY = y;
    };
    const onPointerUp = () => { dragging = false; };
    // Scroll-to-zoom — moves the camera in/out within sane bounds
    const onWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.45 : -0.45;
      camera.position.z = Math.max(8, Math.min(25, camera.position.z + delta));
    };
    canvasEl.addEventListener('mousedown', onPointerDown);
    canvasEl.addEventListener('mousemove', onPointerMove);
    canvasEl.addEventListener('mouseup', onPointerUp);
    canvasEl.addEventListener('mouseleave', onPointerUp);
    canvasEl.addEventListener('touchstart', onPointerDown, { passive: true });
    canvasEl.addEventListener('touchmove', onPointerMove, { passive: true });
    canvasEl.addEventListener('touchend', onPointerUp);
    canvasEl.addEventListener('wheel', onWheel, { passive: false });

    cleanupRef.current = () => {
      disposed = true;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      canvasEl.removeEventListener('mousedown', onPointerDown);
      canvasEl.removeEventListener('mousemove', onPointerMove);
      canvasEl.removeEventListener('mouseup', onPointerUp);
      canvasEl.removeEventListener('mouseleave', onPointerUp);
      canvasEl.removeEventListener('touchstart', onPointerDown);
      canvasEl.removeEventListener('touchmove', onPointerMove);
      canvasEl.removeEventListener('touchend', onPointerUp);
      canvasEl.removeEventListener('wheel', onWheel);
      // Remove THIS effect's renderer canvas from THIS effect's parent.
      // Using `mountEl` (captured at setup) instead of `mountRef.current`
      // (may have changed under React StrictMode's double-invoke) ensures
      // we never leave an orphan canvas behind → fixes the duplicate-
      // canvas artifact visible on /intro in iter_37.
      try { mountEl?.removeChild(renderer.domElement); } catch { /* ignore */ }
      // Belt-and-suspenders: if any canvas slipped past the removeChild
      // above (parent mutated, React remounted, etc.), nuke every canvas
      // still sitting under this mount so the user never sees a second
      // stray globe.
      try {
        const orphans = mountEl?.querySelectorAll('canvas') || [];
        orphans.forEach((n) => n.parentNode?.removeChild(n));
      } catch { /* ignore */ }
      renderer.dispose();
      earthGeom.dispose();
      atmoGeom.dispose();
      cloudsGeom.dispose();
      starGeom.dispose();
    };
    return () => cleanupRef.current?.();
  }, [size, showMoon, showFlightPaths, autoRotate]);

  // ── Social-layer effect (Apr 29 2026 batch #2) ───────────────────
  // Mounts pulsing dots for activeUsers, gold arcs for friendArcs, and
  // semi-transparent dots for the user's own heat signature. Re-runs
  // when the props change (every ~30s when the polling hook returns
  // new data). All meshes attach to the persistent earthGroupRef so
  // they rotate with the planet.
  useEffect(() => {
    const earthGroup = earthGroupRef.current;
    if (!earthGroup) return undefined;

    const EARTH_R = 5;
    const added = []; // collect for cleanup

    // ── Active-earth presence dots ───────────────────────────────
    activeUsers.forEach((u) => {
      if (typeof u?.lat !== 'number' || typeof u?.lon !== 'number') return;
      const pos = latLngToVec3(u.lat, u.lon, EARTH_R * 1.012);
      const color = u.is_authenticated ? 0xffd700 : 0x00f0ff;
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 12, 12),
        new THREE.MeshBasicMaterial({ color }),
      );
      dot.position.copy(pos);
      dot.userData.kind = 'active-user';
      dot.userData.basePhase = Math.random() * Math.PI * 2;
      earthGroup.add(dot);

      // Halo for the pulse
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 12, 12),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35 }),
      );
      halo.position.copy(pos);
      halo.userData.kind = 'active-user-halo';
      halo.userData.basePhase = dot.userData.basePhase;
      earthGroup.add(halo);

      added.push(dot, halo);
    });

    // ── Friend arcs (current user → each family-member lat/lng) ──
    friendArcs.forEach((arc, i) => {
      if (typeof arc?.from_lat !== 'number' || typeof arc?.from_lon !== 'number') return;
      if (typeof arc?.to_lat !== 'number' || typeof arc?.to_lon !== 'number') return;
      const from = latLngToVec3(arc.from_lat, arc.from_lon, EARTH_R);
      const to = latLngToVec3(arc.to_lat, arc.to_lon, EARTH_R);
      const mid = from.clone().add(to).multiplyScalar(0.5);
      const dist = from.distanceTo(to);
      mid.normalize().multiplyScalar(EARTH_R + dist * 0.45);
      const curve = new THREE.QuadraticBezierCurve3(from, mid, to);
      const points = curve.getPoints(64);
      const arcGeom = new THREE.BufferGeometry().setFromPoints(points);
      const arcMat = new THREE.LineBasicMaterial({
        color: 0xffd700,
        transparent: true,
        opacity: 0.85,
      });
      const arcLine = new THREE.Line(arcGeom, arcMat);
      arcLine.userData.kind = 'friend-arc';
      earthGroup.add(arcLine);

      // Gold pellet at the destination so the eye reads "this is where my
      // family lives" — pulses scale.
      const pellet = new THREE.Mesh(
        new THREE.SphereGeometry(0.075, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xffd700 }),
      );
      pellet.position.copy(to);
      pellet.userData.kind = 'friend-pellet';
      pellet.userData.basePhase = i * 0.7;
      earthGroup.add(pellet);
      added.push(arcLine, pellet);
    });

    // ── User heat signature ─────────────────────────────────────
    // Visit-count drives the dot scale (log-normalized 0.04 → 0.12).
    // Color: warm copper to differentiate from the gold friend pellets
    // and the cyan/gold active-user dots.
    if (userHeat.length > 0) {
      const maxCount = Math.max(...userHeat.map((h) => h.visit_count || 1));
      const scaleFor = (c) => 0.04 + 0.08 * (Math.log(1 + (c || 1)) / Math.log(1 + maxCount));
      userHeat.forEach((h) => {
        if (typeof h?.lat !== 'number' || typeof h?.lon !== 'number') return;
        const pos = latLngToVec3(h.lat, h.lon, EARTH_R * 1.008);
        const r = scaleFor(h.visit_count || 1);
        const dot = new THREE.Mesh(
          new THREE.SphereGeometry(r, 10, 10),
          new THREE.MeshBasicMaterial({ color: 0xff8c42, transparent: true, opacity: 0.78 }),
        );
        dot.position.copy(pos);
        dot.userData.kind = 'user-heat';
        earthGroup.add(dot);
        added.push(dot);
      });
    }

    return () => {
      // Cleanup — remove + dispose every mesh we added so we don't
      // leak GPU memory across polling cycles.
      added.forEach((m) => {
        try {
          earthGroup.remove(m);
          if (m.geometry) m.geometry.dispose();
          if (m.material) m.material.dispose();
        } catch { /* ignore */ }
      });
    };
  }, [friendArcs, activeUsers, userHeat]);

  return (
    <div
      ref={mountRef}
      onClick={onClick}
      className={className || 'w-full h-full'}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      data-testid={testId || 'populated-globe'}
    />
  );
}
